import { Apple, GraduationCap, Laptop, Mail, MonitorSmartphone, Receipt, Router, School, Smartphone, Tablet, UserPlus, Users, type LucideIcon } from 'lucide-react';
import { providerPath } from '../lib/integrations';
import type { IntegrationProvider } from '../lib/types';
import { cn } from './ui';

/**
 * Logos come from src/assets/integrations, named after the integration's address: google.svg, jamf-pro.png,
 * apple-school-manager.webp, and so on (see the README there). An integration without a logo file shows an icon.
 */
const FILES = import.meta.glob<string>('../assets/integrations/*.{svg,png,webp}', { eager: true, query: '?url', import: 'default' });

const LOGOS = new Map(Object.entries(FILES).map(([path, url]) => [path.slice(path.lastIndexOf('/') + 1).replace(/\.[a-z]+$/i, ''), url]));

const FALLBACK_ICON: Record<IntegrationProvider, LucideIcon> = {
  BLACKBAUD: Users,
  GOOGLE: Laptop,
  JAMF_PRO: Apple,
  JAMF_SCHOOL: Tablet,
  MOSYLE: Smartphone,
  ARUBA_CENTRAL: Router,
  INTUNE: MonitorSmartphone,
  APPLE_SCHOOL_MANAGER: School,
  EMAIL: Mail,
  FACILITIES_EMAIL: Mail,
  GOOGLE_PROVISIONING: UserPlus,
  ONEROSTER: GraduationCap,
  BLACKBAUD_BILLING: Receipt,
};

/** 48 px on cards, 56 px at the top of an integration's page. */
const SIZE = { md: 'size-12', lg: 'size-14' };

export function IntegrationLogo({ provider, size = 'md', className }: { provider: IntegrationProvider; size?: 'md' | 'lg'; className?: string }) {
  const src = LOGOS.get(providerPath(provider));
  if (src) return <img src={src} alt="" className={cn(SIZE[size], 'shrink-0 object-contain', className)} />;
  const Icon = FALLBACK_ICON[provider];
  return (
    <span className={cn(SIZE[size], 'flex shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700', className)} aria-hidden>
      <Icon className={size === 'lg' ? 'size-7' : 'size-6'} />
    </span>
  );
}
